import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Folder, User } from 'lucide-react';
import clsx from 'clsx';
import { HEADER_HEIGHT, ROW_HEIGHT } from '../utils/dateUtils';
import { useScheduler } from './SchedulerContext';
import { KTIcon } from '../../_metronic/helpers';
import { FormatInvoiceDecimalNumber, getValueFromResource } from '../../common/timeLiveFunction';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import { UpdateToShowModal, UpdateToShowProjectsModal } from './SchedulerSlice';
import { calculateRowHeights } from '../utils/rowHeightCalculator';

const getOverlappingEvents = (events: any[], startDate: Date, viewMode: string) => {
  const eventsByResource = new Map<string, any[]>();

  events.forEach(event => {
    if (event.types !== 'task' && event.types !== 'taskTeam') {
      return;
    }

    if (!eventsByResource.has(event.resourceId)) {
      eventsByResource.set(event.resourceId, []);
    }
    eventsByResource.get(event.resourceId)!.push(event);
  });

  const positionedEvents = new Map<string, any>();

  eventsByResource.forEach((resourceEvents, resourceId) => {
    const sorted = [...resourceEvents].sort((a, b) =>
      a.start.getTime() - b.start.getTime()
    );

    sorted.forEach(event => {
      const overlapping = sorted.filter(e => {
        if (e.id === event.id) return false;

        const eventStart = event.start.getTime();
        const eventEnd = event.end.getTime();
        const eStart = e.start.getTime();
        const eEnd = e.end.getTime();

        return (eventStart <= eEnd && eventEnd >= eStart);
      });

      let lane = 0;
      const usedLanes = new Set<number>();

      overlapping.forEach(e => {
        const pos = positionedEvents.get(e.id);
        if (pos) usedLanes.add(pos.lane);
      });

      while (usedLanes.has(lane)) {
        lane++;
      }

      positionedEvents.set(event.id, {
        ...event,
        lane,
        totalLanes: Math.max(lane + 1, overlapping.length + 1)
      });
    });
  });

  return positionedEvents;
};

export const ResourceTree: React.FC = () => {
  const { flatResources, toggleResource, expandedResourceIds, onResourceClick, startDate, viewMode, events } = useScheduler();
  const { tabs, IsAdd } = useAppSelector((state) => state.scheduler);
  const { cultureInfoName, decimalPlaces } = useAppSelector((state) => state.globalState);
  const isSyncingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const ASSIGNED_COL_WIDTH = 70;
  const positionedEvents = getOverlappingEvents(events, startDate, viewMode);
  const rowHeights = calculateRowHeights(flatResources, events, positionedEvents);
  const contentHeight = rowHeights.reduce((sum, h) => sum + h, 0);
  const maxScrollHeight = 450 - HEADER_HEIGHT;

  useEffect(() => {
    const timeline = document.getElementById('timeline-scroll-container');
    const tree = containerRef.current;
    if (!timeline || !tree) return;

    const handleTimelineScroll = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      tree.scrollTop = timeline.scrollTop;
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    };

    const handleTreeScroll = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      timeline.scrollTop = tree.scrollTop;
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    };

    timeline.addEventListener('scroll', handleTimelineScroll);
    tree.addEventListener('scroll', handleTreeScroll);

    return () => {
      timeline.removeEventListener('scroll', handleTimelineScroll);
      tree.removeEventListener('scroll', handleTreeScroll);
    };
  }, []);

  const handleRowClick = (e: any, resource: any) => {
    e.stopPropagation();
    if (onResourceClick) onResourceClick(resource);
    if (resource.hasChildren || (resource.children && resource.children.length > 0)) {
      toggleResource(resource);
    }
  };

  return (
    <div
      className="flex-shrink-0 border-end bg-white position-sticky start-0 d-flex flex-column h-100"
      style={{
        width: '330px',
        zIndex: 30,
        height: 'auto',
        maxHeight: '450px'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: ASSIGNED_COL_WIDTH,
          width: '1px',
          height: '100%',
          backgroundColor: '#dee2e6',
          zIndex: 2,
        }}
      />
      <div
        style={{ height: HEADER_HEIGHT, paddingTop: '1.54rem', paddingBottom: '1.54rem' }}
        className="d-flex align-items-center border-bottom bg-white"
      >
        <div className="flex-grow-1 px-3 fw-bold text-dark">
          {tabs === 'resource' ? (
            <span className="text-dark fw-bold d-block">{getValueFromResource('Project', dispatch, t)}</span>
          ) : (
            <span className="text-dark fw-bold d-block">{getValueFromResource('Team', dispatch, t)}</span>
          )}
        </div>
        <div
          className="d-flex align-items-center justify-content-center border-start"
          style={{ width: ASSIGNED_COL_WIDTH }}
        >
          <span className="text-dark fw-bold d-block">{getValueFromResource('Assigned', dispatch, t)}</span>

        </div>
      </div>
      <div ref={containerRef} className="flex-grow-1"
        style={{
          overflowY: contentHeight > maxScrollHeight ? 'auto' : 'hidden',
          overflowX: 'hidden',
          height: contentHeight > maxScrollHeight ? maxScrollHeight : contentHeight
        }}
      >
        {flatResources.map((resource: any, index) => {

          const resourceHeight = rowHeights[index];
          if (resource.type === 'add_button' && IsAdd) {
            return (
              <div key={`${resource.id}_${index}`} className="d-flex border-bottom align-items-center" style={{ height: resourceHeight }}>
                <div className="flex-grow-1 d-flex align-items-center"
                  style={{ paddingLeft: resource.depth * 20 + 12 }}>
                  <div
                    // className="d-flex align-items-center text-primary cursor-pointer"
                     className={`d-flex align-items-center ${resource?.enableBtn ? 'text-primary' : 'text-muted'} ${resource?.enableBtn ? 'cursor-pointer' : ''}`}
                    style={{ gap: '8px' }}
                    onClick={() => {
                      if(!resource?.enableBtn) return;
                      if (tabs === 'team') {
                        dispatch(UpdateToShowProjectsModal({ flag: true, employeeId: resource }))
                      } else {
                        dispatch(UpdateToShowModal({ flag: true, resource }))
                      }
                    }}
                  >
                    <span className={`ki-outline ki-plus-circle fs-4 ${resource?.enableBtn ? 'text-primary' : 'text-muted'} ${resource?.enableBtn ? 'cursor-pointer' : ''}`}></span>
                    <span className="fw-normal">{getValueFromResource(`${tabs === 'team' ? 'Add Project' : 'Add Employee'}`, dispatch, t)}</span>
                  </div>
                </div>
                <div className="border-start h-100" style={{ width: ASSIGNED_COL_WIDTH }}></div>
              </div>
            );
          }
          const isExpanded = expandedResourceIds.has(resource.id);
          const showArrow = resource.type === 'project' || resource.type === 'team' || resource.children?.length > 0;
          const assignedHours = resource.TotalAllocatedHours || 0;
          const isOverAllocated = assignedHours > 160;

          return (
            <div
              // key={resource.id}
              key={`${resource.id}_${index}`}
              className="d-flex border-bottom text-sm pointer-event align-items-center"
              style={{ height: resourceHeight }}
              onClick={(e) => handleRowClick(e, resource)}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div
                className="d-flex flex-grow-1 align-items-center overflow-hidden h-100"
                style={{ paddingLeft: resource.depth * 20 + 12 }}
              >
                <div
                  className="me-2 d-flex align-items-center justify-content-center flex-shrink-0 cursor-pointer"
                  style={{ width: 16, height: '100%' }}
                >
                  {showArrow && (
                    isExpanded ? (
                      <ChevronDown
                        size={14}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleResource(resource);
                        }}
                      />
                    ) : (
                      <ChevronRight
                        size={14}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleResource(resource);
                        }}
                      />
                    )
                  )}
                </div>
                <div className="d-flex align-items-center overflow-hidden flex-grow-1 gap-2" style={{ paddingRight: '10px' }}>

                  {(resource.type === 'project' || resource.type === 'teamProject') && (
                    <KTIcon iconName={'some-files'} className='fs-2 text-gray-600 cursor-pointer' />
                  )}

                  {(resource.type === 'person' || resource.type === 'team') && (
                    <KTIcon iconName={'user'} className='fs-5 text-gray-600 cursor-pointer' />
                  )}
                  <div className="d-flex flex-column justify-content-center overflow-hidden" style={{ minWidth: 0 }}>
                    <span
                      className="fw-bold fs-7 text-truncate"
                      style={{ color: 'black', lineHeight: '1.2', display: 'block' }}
                      title={resource.name}
                    >
                      {resource.name}
                    </span>

                    {resource.subtitle && (
                      <span
                        className="text-truncate"
                        style={{
                          fontSize: '11px',
                          color: 'rgb(123, 139, 166)',
                          lineHeight: '1.2',
                          display: 'block'
                        }}
                        title={resource.subtitle}
                      >
                        {resource.subtitle}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center border-start h-100 flex-shrink-0"
                style={{ width: ASSIGNED_COL_WIDTH, backgroundColor: '#fff' }}
              >
                {(resource.type === 'person' || resource.type === 'teamProject') ? (
                  <span className={clsx("fw-bold small", isOverAllocated ? "text-danger" : "text-dark")}>
                    {/* {resource?.TotalAllocatedHours}h */}
                    {FormatInvoiceDecimalNumber(cultureInfoName.toString(), resource?.TotalAllocatedHours, 2)}h
                  </span>
                ) : (resource.type === 'project' || resource.type === 'team') && (
                  // <span className={clsx("fw-bold small", isOverAllocated ? "text-danger" : "text-dark")}>
                  <span className={clsx("fw-bold small text-dark")}>
                    {/* {resource?.TotalAllocatedHours}h */}
                    {FormatInvoiceDecimalNumber(cultureInfoName.toString(), resource?.TotalAllocatedHours, 2)}h
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
